import React, { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { makeRequest } from "@/utils/axiosInterceptor";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, TrendingDown, RefreshCw, ChevronLeft, ChevronRight, X, Coins, Activity, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Alert from "../common/Alert";

const CREDIT_REASONS = ["PURCHASE", "GRANT", "REFUND"] as const;
const DEBIT_REASONS = ["EXPIRED", "CONSUMED"] as const;
const ENTRY_TYPES = [
  { label: "All Types", value: "ALL" },
  { label: "Credit", value: "CREDIT" },
  { label: "Debit", value: "DEBIT" },
];
const REASONS = [
  { label: "All Reasons", value: "ALL" },
  { label: "Purchase", value: "PURCHASE" },
  { label: "Grant", value: "GRANT" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Consumed", value: "CONSUMED" },
  { label: "Refund", value: "REFUND" },
];
const PAGE_LIMITS = [10, 20, 50, 100];

const fmt = (n?: number | null) => (n == null ? "—" : n.toLocaleString("en-IN"));
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(/\b(?:am|pm)\b/gi, (m) => m.toUpperCase()) : "—";

interface LedgerEntry {
  id: number;
  api_user_id: number;
  entry_type: "CREDIT" | "DEBIT";
  tokens: number;
  reason: string;
  balance_after: number;
  tokens_remaining?: number;
  expires_on?: string;
  note?: string;
  created_on: string;
}

interface Batch {
  id: number;
  created_on: string;
  reason: string;
  tokens: number;
  tokens_remaining: number | null;
  expires_on: string | null;
  note: string | null;
}

interface FormFieldProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}

interface TokenDialogProps {
  open: boolean;
  onClose: () => void;
  isDebit: boolean;
  apiUserId: number;
  currentBalance: number;
  onSuccess: (newBalance: number) => void;
  hospitalName?: string;
}

interface DetailModalProps {
  entry: LedgerEntry | null;
  onClose: () => void;
  hospitalList?: any[];
}

interface TableCmpProps {
  headers: string[];
  loading: boolean;
  cols: number;
  children: React.ReactNode;
}

interface TabProps {
  apiUserId: number;
  hospitalList?: any[];
}

interface TokenLedgerDetailsProps {
  selected: any;
  setSelected: (val: any) => void;
  hospitalList?: any[];
  isGlobalView?: boolean;
}

function FormField({ label, required, optional, children }: FormFieldProps) {
  return (
    <div className="space-y-2 mt-4">
      <label className="text-sm font-semibold block">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {optional && <span className="text-xs font-normal text-muted-foreground ml-1">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

function TokenDialog({ open, onClose, isDebit, apiUserId, currentBalance, onSuccess, hospitalName }: TokenDialogProps) {
  const [tokens, setTokens] = useState<string>("");
  const reasons = isDebit ? DEBIT_REASONS : CREDIT_REASONS;
  const [reason, setReason] = useState<string>(reasons[0]);
  const [expiresOn, setExpiresOn] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setTokens("");
      setReason(reasons[0]);
      setExpiresOn("");
      setNote("");
      setIsSubmitting(false);
    }
  }, [open, reasons]);

  const count = parseInt(tokens, 10);
  const isValid =
    !isNaN(count) &&
    count > 0 &&
    (!isDebit || count <= currentBalance) &&
    !!reason &&
    (isDebit || !!expiresOn);

  const isSubmittingRef = React.useRef(false);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const payload: any = {
        api_user_id: apiUserId,
        tokens: count,
        reason,
      };

      if (note.trim()) {
        payload.note = note.trim();
      }
      if (!isDebit && expiresOn) {
        payload.expires_on = new Date(expiresOn).toISOString();
      }

      const res: any = await makeRequest("POST", isDebit ? "/token-ledger/debit" : "/token-ledger/credit", payload);

      if (res?.status === 201 || res?.status === 200 || res?.id) {
        queryClient.invalidateQueries({ queryKey: ["apis"] });
        onSuccess(isDebit ? currentBalance - count : currentBalance + count);
        toast.success(`Successfully ${isDebit ? "debited" : "credited"} ${count.toLocaleString()} tokens.`);
        onClose();
      } else {
        toast.error(res?.message || `Failed to ${isDebit ? "debit" : "credit"} tokens.`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "An unexpected error occurred.");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && !isSubmitting) onClose();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isDebit ? <TrendingDown size={18} /> : <CreditCard size={18} />}
              {isDebit ? "Debit" : "Credit"} Tokens
            </DialogTitle>
            <DialogDescription>
              {isDebit ? `Deduct tokens from ${hospitalName || `API User #${apiUserId}`}.` : `Add tokens to ${hospitalName || `API User #${apiUserId}`}.`} Current balance:{" "}
              <strong>{fmt(currentBalance)}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="py-3 space-y-1">
            <FormField label={`Tokens to ${isDebit ? "Debit" : "Credit"}`} required>
              <Input
                type="number"
                min={1}
                max={isDebit ? currentBalance : undefined}
                placeholder={isDebit ? `Max ${fmt(currentBalance)}` : "e.g. 500"}
                value={tokens}
                onChange={(e) => setTokens(e.target.value)}
                disabled={isSubmitting}
              />
              {tokens && !isNaN(count) && isDebit && count > currentBalance && (
                <p className="text-sm text-red-500 mt-1">Exceeds current balance</p>
              )}
              {isValid && (
                <p className="text-sm text-muted-foreground mt-1">
                  New balance:{" "}
                  <strong>{fmt(isDebit ? currentBalance - count : currentBalance + count)}</strong>
                </p>
              )}
            </FormField>

            <FormField label="Reason" required>
              <Select value={reason} onValueChange={setReason} disabled={isSubmitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            {!isDebit && (
              <FormField label="Expiry Date" required>
                <Input
                  type="datetime-local"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                  disabled={isSubmitting}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </FormField>
            )}

            <FormField label="Note" optional>
              <Input
                placeholder={isDebit ? "e.g. Correction error" : "e.g. Free tokens"}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isSubmitting}
                maxLength={255}
              />
            </FormField>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant={isDebit ? "destructive" : "default"}
              onClick={() => setConfirmOpen(true)}
              disabled={!isValid || isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : isDebit ? (
                <TrendingDown size={14} />
              ) : (
                <CreditCard size={14} />
              )}
              {isDebit ? "Debit" : "Credit"} {isValid ? fmt(count) : ""} Tokens
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Alert
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={`Confirm ${isDebit ? "Debit" : "Credit"}`}
        description={`You are about to ${isDebit ? "debit" : "credit"} ${fmt(count)} tokens ${isDebit ? "from" : "to"} ${hospitalName || `API User #${apiUserId}`}. Are you sure?`}
        action={handleSubmit}
      />
    </>
  );
}

function DetailModal({ entry, onClose, hospitalList }: DetailModalProps) {
  if (!entry) return null;

  const rows = [
    { label: "Ledger ID", value: `#${entry.id}` },
    { label: "Hospital Name", value: hospitalList?.find((h: any) => h.api?.id === entry.api_user_id)?.name || `#${entry.api_user_id}` },
    {
      label: "Type",
      value: entry.entry_type === "CREDIT" ? (
        <Badge>CREDIT</Badge>
      ) : (
        <Badge variant="destructive">DEBIT</Badge>
      ),
    },
    { label: "Tokens", value: fmt(entry.tokens) },
    { label: "Reason", value: entry.reason },
    { label: "Balance After", value: fmt(entry.balance_after) },
    ...(entry.entry_type === "CREDIT"
      ? [
          { label: "Tokens Remaining", value: fmt(entry.tokens_remaining) },
          { label: "Expires On", value: fmtDate(entry.expires_on) },
        ]
      : []),
    { label: "Note", value: entry.note || "—" },
    { label: "Created On", value: fmtDate(entry.created_on) },
  ];

  return (
    <Dialog
      open={!!entry}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Transaction Details — #{entry.id}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col divide-y rounded-md border">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between px-4 py-3">
              <span className="text-sm font-medium text-muted-foreground">{r.label}</span>
              <span className="text-sm font-semibold">{r.value}</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X size={14} className="mr-2" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TableCmp({ headers, loading, cols, children }: TableCmpProps) {
  return (
    <div className="rounded-lg border overflow-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            {headers.map((h) => (
              <TableHead
                key={h}
                className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-center"
              >
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={cols} className="text-center py-12">
                <Loader2 className="animate-spin inline" size={22} />
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ActiveBatches({ apiUserId }: TabProps) {
  const { data: batches = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["active-batches", apiUserId],
    queryFn: async () => {
      const res: any = await makeRequest("GET", `/token-ledger/api-user/${apiUserId}/active-batches`, null);
      return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
    },
    enabled: !!apiUserId,
  });

  const loading = isLoading || isFetching;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold tracking-tight">Active Token Batches</h3>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={loading}>
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <TableCmp
        headers={["ID", "Credited On", "Reason", "Credited", "Remaining", "Expires", "Note"]}
        loading={loading}
        cols={7}
      >
        {!batches.length ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
              No active token batches
            </TableCell>
          </TableRow>
        ) : (
          batches.map((b: Batch) => {
            const isExpired = b.expires_on && new Date(b.expires_on) < new Date();
            return (
              <TableRow key={b.id} className="hover:bg-muted/30">
                <TableCell className="text-muted-foreground font-mono text-xs text-center">
                  #{b.id}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-center">
                  {fmtDate(b.created_on)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className="text-xs">
                    {b.reason}
                  </Badge>
                </TableCell>
                <TableCell className="tabular-nums font-medium text-center">
                  {fmt(b.tokens)}
                </TableCell>
                <TableCell className="text-center">
                  <span className="text-green-600 font-semibold tabular-nums">
                    {fmt(b.tokens_remaining)}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap text-center">
                  {b.expires_on ? (
                    <span className={isExpired ? "text-red-500 font-medium" : "text-sm"}>
                      {fmtDate(b.expires_on)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs italic">No expiry</span>
                  )}
                </TableCell>
                <TableCell
                  className="max-w-[160px] truncate text-sm text-muted-foreground mx-auto text-center"
                  title={b.note || ""}
                >
                  {b.note || "—"}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableCmp>
    </div>
  );
}

function TransactionHistory({ apiUserId, hospitalList }: TabProps) {
  const [detailEntry, setDetailEntry] = useState<LedgerEntry | null>(null);
  const [fType, setFType] = useState<string>("ALL");
  const [fReason, setFReason] = useState<string>("ALL");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = async (period: "last_month" | "current_month") => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams({ period });
      if (apiUserId) {
        params.set("apiUserId", String(apiUserId));
      }
      const res: any = await makeRequest("GET", `/token-ledger/export?${params}`, null, { responseType: "blob" });
      
      let blob;
      let filename = `token-ledger-${period}.csv`;

      if (res?.message && !res?.data) {
        throw new Error(res.message);
      }

      if (res?.headers && res.headers["content-disposition"]) {
        const match = res.headers["content-disposition"].match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      blob = res?.data || res;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: "text/csv" });
      }

      if (blob.size === 0) {
        toast.error("No token ledger records found for this period.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err: any) {
      console.error(err);
      toast.error("Unable to download the token ledger. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };


  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [fType, fReason, limit, apiUserId]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["ledger-entries", apiUserId, page, limit, fType, fReason],
    queryFn: async () => {
      const p = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (apiUserId) p.set("apiUserId", String(apiUserId));
      if (fType !== "ALL") p.set("entryType", fType);
      if (fReason !== "ALL") p.set("reason", fReason);

      const res: any = await makeRequest("GET", `/token-ledger?${p}`, null);
      
      const responseData = res?.data?.entries ? res.data : res?.entries ? res : null;
      return responseData && Array.isArray(responseData.entries)
        ? responseData
        : { entries: Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [] };
    },
  });

  const loading = isLoading || isFetching;
  const entries: LedgerEntry[] = data?.entries ?? [];
  const totalP = data?.totalPages ?? 1;
  const totalC = data?.totalCount ?? 0;

  const filters = [
    { val: fType, set: setFType, opts: ENTRY_TYPES, w: 140 },
    { val: fReason, set: setFReason, opts: REASONS, w: 150 },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-base font-semibold tracking-tight">Transaction History</h3>
        <div className="flex gap-2 items-center flex-wrap">
          {filters.map((f, i) => (
            <Select key={i} value={f.val} onValueChange={f.set}>
              <SelectTrigger className="h-9 text-sm" style={{ width: f.w }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {f.opts.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}

          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-[110px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_LIMITS.map((l) => (
                <SelectItem key={l} value={String(l)}>
                  {l} / Page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={loading || isDownloading}
                className="text-xs h-9"
              >
                {isDownloading ? (
                  <Loader2 size={13} className="mr-1.5 animate-spin" />
                ) : (
                  <Download size={13} className="mr-1.5" />
                )}
                {isDownloading ? "Downloading..." : "Download CSV"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleDownload("last_month")}>
                Last Month
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload("current_month")}>
                Current Month
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
            className="text-xs h-9"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <TableCmp
        headers={!apiUserId ? ["Date", "Hospital Name", "Tokens", "Reason", "Balance After", "Note"] : ["Date", "Tokens", "Reason", "Balance After", "Note"]}
        loading={loading}
        cols={!apiUserId ? 6 : 5}
      >
        {!Array.isArray(entries) || entries.length === 0 ? (
          <TableRow>
            <TableCell colSpan={!apiUserId ? 6 : 5} className="text-center py-12 text-muted-foreground">
              No transactions found
            </TableCell>
          </TableRow>
        ) : (
          entries.map((e) => (
            <TableRow
              key={e.id}
              onClick={() => setDetailEntry(e)}
              className="cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <TableCell className="whitespace-nowrap text-sm text-center">
                {fmtDate(e.created_on)}
              </TableCell>
              {!apiUserId && (
                <TableCell className="text-center font-medium text-xs text-muted-foreground">
                  {hospitalList?.find((h: any) => h.api?.id === e.api_user_id)?.name || `#${e.api_user_id}`}
                </TableCell>
              )}
              <TableCell
                className={`text-center tabular-nums font-semibold ${
                  e.entry_type === "CREDIT" ? "text-green-600" : "text-red-500"
                }`}
              >
                {e.entry_type === "CREDIT" ? "+" : "−"}
                {fmt(e.tokens)}
              </TableCell>
              <TableCell className="text-center">
                <Badge variant="outline" className="text-xs">
                  {e.reason}
                </Badge>
              </TableCell>
              <TableCell className="text-center tabular-nums font-medium">
                {fmt(e.balance_after)}
              </TableCell>
              <TableCell
                className="max-w-[180px] truncate text-sm text-muted-foreground mx-auto text-center"
                title={e.note || ""}
              >
                {e.note || "—"}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableCmp>

      <div className="sticky bottom-[-16px] bg-background py-3 border-t mt-2 flex items-center justify-between text-sm text-muted-foreground px-1 z-10">
        <span>
          {totalC > 0
            ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, totalC)} of ${totalC.toLocaleString()} entries`
            : "No entries"}
        </span>
        <div className="flex gap-1.5 items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="px-3 font-medium text-foreground">
            {page} / {Math.max(totalP, 1)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalP || loading}
            onClick={() => setPage((p) => p + 1)}
            className="h-8 w-8 p-0"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <DetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} hospitalList={hospitalList} />
    </div>
  );
}

export default function TokenLedgerDetails({ selected, setSelected, hospitalList, isGlobalView }: TokenLedgerDetailsProps) {
  const apiUserId = selected?.api?.id;
  const [localBalance, setLocalBalance] = useState<number>(selected?.api?.allowed_requests ?? 0);
  const [dialogType, setDialogType] = useState<"CREDIT" | "DEBIT" | null>(null);
  const [activeTab, setActiveTab] = useState<"batches" | "history">("history");
  const [hospitalSearch, setHospitalSearch] = useState("");
  const queryClient = useQueryClient();
  const { data: activeBatches = [], isSuccess: isBatchesLoaded } = useQuery({
    queryKey: ["active-batches", apiUserId],
    queryFn: async () => {
      const res: any = await makeRequest("GET", `/token-ledger/api-user/${apiUserId}/active-batches`, null);
      return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
    },
    enabled: !!apiUserId,
  });

  useEffect(() => {
    if (isBatchesLoaded) {
      const trueBalance = activeBatches.reduce((sum: number, b: Batch) => sum + (b.tokens_remaining ?? 0), 0);
      setLocalBalance(trueBalance);
    } else {
      setLocalBalance(selected?.api?.allowed_requests ?? 0);
    }
  }, [activeBatches, isBatchesLoaded, selected?.api?.allowed_requests]);

  const handleSuccess = useCallback(
    (newBalance: number) => {
      setLocalBalance(newBalance);
      queryClient.invalidateQueries({ queryKey: ["active-batches", apiUserId] });
      queryClient.invalidateQueries({ queryKey: ["ledger-entries", apiUserId] });
      setSelected({ ...selected, api: { ...selected.api, allowed_requests: newBalance } });
    },
    [selected, setSelected, apiUserId, queryClient]
  );

  if (!isGlobalView && (!selected?.api || apiUserId == null)) {
    return (
      <div className="w-full h-[70vh] flex justify-center items-center border rounded-lg bg-muted/10">
        <span className="text-muted-foreground">No API configured for this hospital</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {isGlobalView && (
        <div className="flex items-center gap-3 bg-muted/30 p-3 rounded-lg border">
          <span className="text-sm font-medium">Select Hospital:</span>
          <Select
            value={selected?.id?.toString() || "all"}
            onValueChange={(val) => {
              if (val === "all") {
                setSelected(null);
                setActiveTab("history"); // Ensure we show history globally
              } else {
                const h = hospitalList?.find((x) => x.id.toString() === val);
                setSelected(h || null);
              }
            }}
            onOpenChange={(isOpen) => !isOpen && setHospitalSearch("")}
          >
            <SelectTrigger className="w-[300px] bg-background">
              <SelectValue placeholder="All Hospitals" />
            </SelectTrigger>
            <SelectContent>
              <div className="p-2 border-b mb-1">
                <Input
                  placeholder="Search hospital..."
                  value={hospitalSearch}
                  onChange={(e) => setHospitalSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="h-8 text-sm"
                />
              </div>
              <SelectItem value="all">All Hospitals</SelectItem>
              {Array.isArray(hospitalList) && hospitalList
                .filter((h: any) => h.api)
                .filter((h: any) => h.name.toLowerCase().includes(hospitalSearch.toLowerCase()))
                .map((h: any) => (
                <SelectItem key={h.id} value={h.id.toString()}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {apiUserId && (
        <div className="flex items-center justify-between flex-wrap gap-4 py-2 border px-6 rounded-lg bg-card shadow-sm">
          <div className="flex gap-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Coins size={22} className="text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Remaining Tokens
              </p>
              <p className="text-2xl font-bold tabular-nums leading-tight mt-0.5">
                {fmt(localBalance)}
              </p>
            </div>
          </div>
            
          {selected?.api?.segregation_model_access && (
            <div className="flex items-center gap-4 border-l pl-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                <Activity size={22} className="text-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Uploaded CXRs
                </p>
                <p className="text-2xl font-bold tabular-nums leading-tight mt-0.5">
                  {fmt(selected?.api?.chest_xray_count ?? 0)}
                </p>
              </div>
            </div>
          )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => setDialogType("CREDIT")}
              className="flex items-center gap-2"
            >
              <CreditCard size={14} /> + Credit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setDialogType("DEBIT")}
              disabled={localBalance <= 0}
              className="flex items-center gap-2"
            >
              <TrendingDown size={14} /> − Debit
            </Button>
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex border-b border-border w-full">
          {[
            { id: "batches", label: "Active Token Batches", hidden: !apiUserId },
            { id: "history", label: "Transaction History" },
          ].map((tab) => (
            !tab.hidden && (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as "batches" | "history")}
                className={`flex-1 px-5 py-3 text-sm font-medium border-b-2 transition-all outline-none ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                {tab.label}
              </button>
            )
          ))}
        </div>

        <div className="pt-2">
          {apiUserId && (
            <div className={activeTab === "batches" ? "block" : "hidden"}>
              <ActiveBatches apiUserId={apiUserId} />
            </div>
          )}
          <div className={activeTab === "history" ? "block" : "hidden"}>
            <TransactionHistory apiUserId={apiUserId} hospitalList={hospitalList} />
          </div>
        </div>
      </div>

      <TokenDialog
        open={!!dialogType}
        isDebit={dialogType === "DEBIT"}
        onClose={() => setDialogType(null)}
        apiUserId={apiUserId}
        currentBalance={localBalance}
        onSuccess={handleSuccess}
        hospitalName={selected?.name}
      />
    </div>
  );
}
